import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import { useWorkflowValidationContext } from '../components/shared';

const REDIRECT_DELAY = 1500;

/**
 * useWorkflowForm – Composed hook that eliminates duplicated logic
 * between WorkflowCreatePage and WorkflowDetailPage.
 *
 * Must be called inside a WorkflowValidationProvider.
 */
export const useWorkflowForm = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentWorkflow = useAppSelector(state => state.workflow.currentWorkflow);
  const { triggerValidation } = useWorkflowValidationContext();
  const { showDialog, confirmLeave, cancelLeave, guardedNavigate } = useUnsavedChangesGuard();
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const clearError = useCallback(() => setErrorMessage(''), []);

  /**
   * Runs validation then delegates to the caller-provided async submit function.
   * On success shows a toast and navigates to the list after a short delay.
   */
  const validateAndSubmit = useCallback(async (
    submitFn: () => Promise<void>,
    fallbackError = 'Operation failed. Please try again.',
  ) => {
    setErrorMessage('');
    if (!currentWorkflow) return;
    if (!triggerValidation()) return;
    try {
      await submitFn();
      setShowSuccess(true);
      setTimeout(() => navigate('/workflows'), REDIRECT_DELAY);
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'data' in error
        ? (error as { data?: { message?: string } }).data?.message
        : undefined;
      setErrorMessage(message || fallbackError);
    }
  }, [currentWorkflow, triggerValidation, navigate]);

  return {
    dispatch,
    navigate,
    currentWorkflow,
    showSuccess,
    setShowSuccess,
    errorMessage,
    clearError,
    validateAndSubmit,
    showDialog,
    confirmLeave,
    cancelLeave,
    guardedNavigate,
  };
};
